import { useTranslation as usePublicI18n } from "react-i18next";
import { publicText, publicTemplate } from "../publicText";
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeftOutlined, ArrowRightOutlined, CheckCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Reveal } from "./PageUtilities";
export function CaseStudyBreadcrumb({
  current
}) {
  usePublicI18n();
  return <nav className="science-breadcrumb" aria-label={publicText("パンくずリスト")}>
      <Link to="/case-studies">
        <ArrowLeftOutlined />{publicText("Case Studies")}</Link>
      <span>{publicText("/")}</span>
      <span>{publicText("Case Study ")}{publicText(current)}</span>
    </nav>;
}
export function SectionHeader({
  index,
  eyebrow,
  title,
  description,
  align = "left"
}) {
  usePublicI18n();
  return <Reveal className={`science-section-header is-${align}`}>
      <div className="science-section-header__meta">
        {publicText(index && <span className="science-section-index">{publicText(index)}</span>)}
        {publicText(eyebrow && <span className="science-eyebrow">{publicText(eyebrow)}</span>)}
      </div>

      <h2>{publicText(title)}</h2>
      {publicText(description && <p>{publicText(description)}</p>)}
    </Reveal>;
}
export function KeyFinding({
  label = "KEY FINDING",
  title,
  children
}) {
  usePublicI18n();
  return <Reveal className="science-key-finding">
      <span>{publicText(label)}</span>
      <h3>{publicText(title)}</h3>
      {publicText(children && <div className="science-key-finding__body">{publicText(children)}</div>)}
    </Reveal>;
}
export function MetricCard({
  label,
  value,
  unit,
  caption,
  tone = "neutral",
  icon
}) {
  usePublicI18n();
  return <div className={`science-metric-card tone-${tone}`}>
      <div className="science-metric-card__top">
        {publicText(icon && <span className="science-metric-card__icon">{publicText(icon)}</span>)}
        <span className="science-metric-card__label">{publicText(label)}</span>
      </div>
      <div className="science-metric-card__value">
        {publicText(value)}
        {publicText(unit && <small>{publicText(unit)}</small>)}
      </div>
      {publicText(caption && <p>{publicText(caption)}</p>)}
    </div>;
}
export function Figure({
  number,
  title,
  src,
  alt,
  caption,
  contain = false,
  className = "",
  labels = []
}) {
  usePublicI18n();
  return <Reveal className={`science-figure ${className}`.trim()}>
      <div className={`science-figure__media ${contain ? "is-contain" : ""}`}>
        <img src={src} alt={publicText(alt)} />

        {publicText(labels.length > 0 && <div className="science-figure__labels" aria-hidden="true">
            {publicText(labels.map(label => <span key={label}>{publicText(label)}</span>))}
          </div>)}
      </div>

      <figcaption>
        <div className="science-figure__heading">
          {publicText(number && <span>{publicText("FIG. ")}{publicText(number)}</span>)}
          {publicText(title && <strong>{publicText(title)}</strong>)}
        </div>
        {publicText(caption && <p>{publicText(caption)}</p>)}
      </figcaption>
    </Reveal>;
}
export function TwoFigureGrid({
  children
}) {
  usePublicI18n();
  return <div className="science-two-figure-grid">{publicText(children)}</div>;
}
export function ScientificNote({
  title,
  children,
  tone = "info",
  compact = false
}) {
  usePublicI18n();
  const icons = {
    info: <InfoCircleOutlined />,
    caution: <ExclamationCircleOutlined />,
    success: <CheckCircleOutlined />
  };
  return <Reveal className={`science-note tone-${tone} ${compact ? "is-compact" : ""}`}>
      <div className="science-note__icon">{publicText(icons[tone] || icons.info)}</div>
      <div>
        {publicText(title && <strong>{publicText(title)}</strong>)}
        <div className="science-note__body">{publicText(children)}</div>
      </div>
    </Reveal>;
}
export function DataTable({
  columns,
  rows,
  caption,
  compact = false
}) {
  usePublicI18n();
  return <Reveal className={`science-table-wrap ${compact ? "is-compact" : ""}`}>
      <div className="science-table-scroll">
        <table className="science-data-table">
          {publicText(caption && <caption>{publicText(caption)}</caption>)}
          <thead>
            <tr>
              {publicText(columns.map(column => <th key={column.key} scope="col">
                  {publicText(column.label)}
                  {publicText(column.unit && <small>{publicText(column.unit)}</small>)}
                </th>))}
            </tr>
          </thead>
          <tbody>
            {publicText(rows.map((row, rowIndex) => <tr key={row.key || row.id || row.time || rowIndex}>
                {publicText(columns.map(column => <td key={column.key} className={column.align ? `is-${column.align}` : ""}>
                    {publicText(column.render ? column.render(row[column.key], row) : row[column.key])}
                  </td>))}
              </tr>))}
          </tbody>
        </table>
      </div>
    </Reveal>;
}
export function MethodGrid({
  items
}) {
  usePublicI18n();
  return <Reveal className="science-method-grid">
      {publicText(items.map((item, index) => <div key={`${item.label}-${index}`}>
          <span>{publicText(item.label)}</span>
          <strong>{publicText(item.value)}</strong>
          {publicText(item.note && <p>{publicText(item.note)}</p>)}
        </div>))}
    </Reveal>;
}
export function StudyTimeline({
  items
}) {
  usePublicI18n();
  return <Reveal className="science-timeline">
      {publicText(items.map((item, index) => <div className="science-timeline__item" key={`${item.time}-${index}`}>
          <div className="science-timeline__marker">
            <span>{publicText(String(index + 1).padStart(2, "0"))}</span>
          </div>
          <div className="science-timeline__content">
            <small>{publicText(item.time)}</small>
            <strong>{publicText(item.title)}</strong>
            <p>{publicText(item.text)}</p>
          </div>
        </div>))}
    </Reveal>;
}
export function DefinitionList({
  items
}) {
  usePublicI18n();
  return <Reveal className="science-definition-list">
      {publicText(items.map(item => <div key={item.term}>
          <dt>{publicText(item.term)}</dt>
          <dd>{publicText(item.description)}</dd>
        </div>))}
    </Reveal>;
}
export function CaseNavigation({
  previous,
  next
}) {
  usePublicI18n();
  return <nav className="science-case-navigation" aria-label={publicText("Case Study navigation")}>
      {publicText(previous ? <Link to={previous.path} className="science-case-navigation__item is-prev">
          <span>
            <ArrowLeftOutlined />{publicText(" PREVIOUS")}</span>
          <strong>{publicText("CASE ")}{publicText(previous.id)}
            <br />
            {publicText(previous.title)}
          </strong>
        </Link> : <div />)}

      {publicText(next ? <Link to={next.path} className="science-case-navigation__item is-next">
          <span>{publicText("NEXT ")}<ArrowRightOutlined />
          </span>
          <strong>{publicText("CASE ")}{publicText(next.id)}
            <br />
            {publicText(next.title)}
          </strong>
        </Link> : <Link to="/case-studies" className="science-case-navigation__item is-next">
          <span>{publicText("INDEX ")}<ArrowRightOutlined />
          </span>
          <strong>{publicText("\u3059\u3079\u3066\u306ECase Study\u3092\u898B\u308B")}</strong>
        </Link>)}
    </nav>;
}
